import { vec2, type Vec2 } from '../math/vec2.ts';
import type { Renderer } from '../rendering/Renderer.ts';
import type { Magnet } from '../simulation/Magnet.ts';

/**
 * Pointer gestures.
 *
 * The whole interaction model is four gestures on one surface:
 *   tap empty space   -> place a magnet
 *   tap a magnet      -> flip its polarity
 *   drag a magnet     -> move it
 *   long-press        -> remove it
 *
 * Tap and drag are separated by a movement threshold in CSS pixels (fingers wobble), and
 * the long press is cancelled by movement so a drag never deletes what you meant to move.
 * Timing is driven from the game loop rather than setTimeout, which also gives the
 * renderer a progress value for the hold ring for free.
 */

/** Movement beyond this many CSS pixels turns a tap into a drag. */
export const DRAG_THRESHOLD_PX = 8;
/** Hold this long on a magnet to remove it. */
export const LONG_PRESS_MS = 500;

export interface InputHandlers {
  /** Fired on every pointer down, before anything is decided. Unlocks audio. */
  onGesture(): void;
  hitTest(point: Vec2): Magnet | null;
  onTapEmpty(point: Vec2): void;
  onTapMagnet(magnet: Magnet): void;
  onDragStart(magnet: Magnet): void;
  onDrag(magnet: Magnet, point: Vec2): void;
  onDragEnd(magnet: Magnet): void;
  onLongPress(magnet: Magnet): void;
}

export class Input {
  /** The magnet currently under the active pointer, if any. */
  magnet: Magnet | null = null;
  dragging = false;
  /** 0..1 towards long-press removal. */
  holdProgress = 0;
  /** True while any pointer is down — used to pause "tap to place" hints. */
  active = false;

  private pointerId: number | null = null;
  private downAt = 0;
  private movedBeyondThreshold = false;
  private handled = false;
  private readonly startScreen = vec2();
  private readonly grabOffset = vec2();
  private readonly worldPoint = vec2();

  private readonly listeners: Array<() => void> = [];

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly renderer: Renderer,
    private readonly handlers: InputHandlers,
  ) {
    this.attach();
  }

  private attach(): void {
    const canvas = this.canvas;

    const down = (event: PointerEvent) => this.onPointerDown(event);
    const move = (event: PointerEvent) => this.onPointerMove(event);
    const up = (event: PointerEvent) => this.onPointerUp(event, false);
    const cancel = (event: PointerEvent) => this.onPointerUp(event, true);
    // iOS Safari still fires its own pinch/double-tap zoom gestures over a canvas.
    const blockGesture = (event: Event) => event.preventDefault();

    canvas.addEventListener('pointerdown', down);
    // Move and up go on the window so a drag survives leaving the canvas.
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    canvas.addEventListener('gesturestart', blockGesture);
    canvas.addEventListener('touchstart', blockGesture, { passive: false });
    canvas.addEventListener('contextmenu', blockGesture);

    this.listeners.push(
      () => canvas.removeEventListener('pointerdown', down),
      () => window.removeEventListener('pointermove', move),
      () => window.removeEventListener('pointerup', up),
      () => window.removeEventListener('pointercancel', cancel),
      () => canvas.removeEventListener('gesturestart', blockGesture),
      () => canvas.removeEventListener('touchstart', blockGesture),
      () => canvas.removeEventListener('contextmenu', blockGesture),
    );
  }

  private toWorld(event: PointerEvent): Vec2 {
    const bounds = this.canvas.getBoundingClientRect();
    return this.renderer.screenToWorld(
      event.clientX - bounds.left,
      event.clientY - bounds.top,
      this.worldPoint,
    );
  }

  private onPointerDown(event: PointerEvent): void {
    // One pointer at a time: a second finger during a drag is ignored, not obeyed.
    if (this.pointerId !== null) return;
    if (event.button !== 0 && event.pointerType === 'mouse') return;

    event.preventDefault();
    this.handlers.onGesture();
    this.pointerId = event.pointerId;
    this.canvas.setPointerCapture?.(event.pointerId);

    this.active = true;
    this.downAt = performance.now();
    this.movedBeyondThreshold = false;
    this.handled = false;
    this.dragging = false;
    this.holdProgress = 0;

    const bounds = this.canvas.getBoundingClientRect();
    this.startScreen.x = event.clientX - bounds.left;
    this.startScreen.y = event.clientY - bounds.top;

    const point = this.toWorld(event);
    this.magnet = this.handlers.hitTest(point);
    if (this.magnet) {
      this.grabOffset.x = this.magnet.position.x - point.x;
      this.grabOffset.y = this.magnet.position.y - point.y;
    }
  }

  private onPointerMove(event: PointerEvent): void {
    if (event.pointerId !== this.pointerId) return;
    event.preventDefault();

    const bounds = this.canvas.getBoundingClientRect();
    const dx = event.clientX - bounds.left - this.startScreen.x;
    const dy = event.clientY - bounds.top - this.startScreen.y;

    if (!this.movedBeyondThreshold && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
      this.movedBeyondThreshold = true;
      this.holdProgress = 0;
      if (this.magnet && !this.handled) {
        this.dragging = true;
        this.handlers.onDragStart(this.magnet);
      }
    }

    if (this.dragging && this.magnet) {
      const point = this.toWorld(event);
      point.x += this.grabOffset.x;
      point.y += this.grabOffset.y;
      this.handlers.onDrag(this.magnet, point);
    }
  }

  private onPointerUp(event: PointerEvent, cancelled: boolean): void {
    if (event.pointerId !== this.pointerId) return;

    this.canvas.releasePointerCapture?.(event.pointerId);
    this.pointerId = null;
    this.active = false;

    const magnet = this.magnet;
    const wasDragging = this.dragging;
    const handled = this.handled;
    const moved = this.movedBeyondThreshold;

    this.magnet = null;
    this.dragging = false;
    this.holdProgress = 0;

    if (cancelled) {
      if (wasDragging && magnet) this.handlers.onDragEnd(magnet);
      return;
    }

    if (wasDragging && magnet) {
      this.handlers.onDragEnd(magnet);
      return;
    }

    if (handled || moved) return;

    if (magnet) {
      this.handlers.onTapMagnet(magnet);
    } else {
      this.handlers.onTapEmpty(this.toWorld(event));
    }
  }

  /** Called once per frame; fires the long press and updates its progress. */
  update(now: number): void {
    if (this.pointerId === null || !this.magnet || this.handled || this.movedBeyondThreshold) {
      if (this.pointerId === null) this.holdProgress = 0;
      return;
    }

    const held = now - this.downAt;
    this.holdProgress = Math.min(1, held / LONG_PRESS_MS);

    if (held >= LONG_PRESS_MS) {
      const magnet = this.magnet;
      this.handled = true;
      this.holdProgress = 0;
      this.magnet = null;
      this.handlers.onLongPress(magnet);
    }
  }

  destroy(): void {
    for (const remove of this.listeners) remove();
    this.listeners.length = 0;
  }
}
